import React, { useEffect, useRef, useState } from "react";
import CustomButton from "./CustomButton";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "src/app/services/store";
import { eymemoryLogout, logout } from "src/shared/redux-state/src";
import { EymemoryRoutes } from "src/app/navigation/routes";
import { toast } from "react-toastify";
import { TOAST_AUTO_CLOSE_TIMER } from "src/shared/config/src/lib/constants";
import LocalImages from "../../static";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { trackOrderStatus } from "src/shared/services/src";
import { EymemoriesStageSteps } from "src/shared/types/src";
import { isMobile, isTablet } from "react-device-detect";

const getFilenameFromUrl = (url: string): string | null => {
  try {
    const pathname = new URL(url).pathname;
    const decoded = decodeURIComponent(pathname);
    return decoded.substring(decoded.lastIndexOf("/") + 1);
  } catch {
    return null;
  }
};

type NegativeCountdownDownloadProps = {
  startTimer?: number;
  downloadUrl: string;
  orderHash: string;
};

const NegativeCountdownDownload: React.FC<NegativeCountdownDownloadProps> = ({
  startTimer = 5,
  downloadUrl,
  orderHash,
}) => {
  const dispatch = useAppDispatch();
  // const [secondsLeft, setSecondsLeft] = useState(startTimer);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const shouldBlockFactorsRef = useRef({
    isMobile: isMobile,
    isTablet: isTablet,
    downloading: downloading,
    hasDownloaded: hasDownloaded,
  });
  // const isReDownloaded = useRef(false);
  const navigate = useNavigate();

  const handleDownload = async () => {
    try {
      if (!(isMobile || isTablet)) setDownloading(() => true);
      setProgress(0);

      if (!downloadUrl) throw new Error("Download link not available!");

      await trackOrderStatus({
        status: EymemoriesStageSteps.DOWNLOADED,
        orderHash,
      });

      // Check for mobile browsers and redirect for native download
      if (isMobile || isTablet) {
        window.location.href = downloadUrl;
        setDownloading(false);
        setHasDownloaded(true);
        return;
      }

      const response = await fetch(downloadUrl);

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          const errorMessage =
            response.status === 401
              ? "Session expired. Please login again."
              : "Access denied. You have been logged out.";

          toast.error(errorMessage, { autoClose: TOAST_AUTO_CLOSE_TIMER });
          setDownloading(false);
          await dispatch(eymemoryLogout({ orderHash }));
          navigate(`${EymemoryRoutes.EYMEMORIES_ERROR}?orderHash=${orderHash}`);
          return;
        }

        const error = new Error(response.statusText);
        (error as any).status = response.status;
        throw error;
      }

      const contentLength = response.headers.get("Content-Length");

      if (!contentLength) {
        throw new Error("Cannot determine file size for progress tracking");
      }

      const total = parseInt(contentLength, 10);
      let loaded = 0;

      const reader = response.body?.getReader();
      const chunks: Uint8Array[] = [];

      while (true) {
        const { done, value } = await reader!.read();
        if (done) break;
        if (value) {
          chunks.push(value);
          loaded += value.length;
          setProgress(Math.floor((loaded / total) * 100));
        }
      }

      // Combine chunks into a single Blob
      const blob = new Blob(chunks);
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = getFilenameFromUrl(downloadUrl) || "eyworks-download.zip";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      setProgress(100);
      setHasDownloaded(() => true);
      setDownloading(() => false);
    } catch (err: any) {
      setDownloading(false);
      toast.error(err.message || "Download failed.");
    }
  };

  useEffect(() => {
    shouldBlockFactorsRef.current = {
      isMobile: isMobile,
      isTablet: isTablet,
      downloading: downloading,
      hasDownloaded: hasDownloaded,
    };
  }, [isMobile, isTablet, downloading, hasDownloaded]);

  useEffect(() => {
    const message =
      "Are you sure you want to leave this page? Your download may be interrupted.";

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const { isMobile, isTablet, downloading, hasDownloaded } =
        shouldBlockFactorsRef.current;
      if (!(isMobile || isTablet) && (downloading || !hasDownloaded)) {
        event.preventDefault();
        event.returnValue = message;
      }
    };

    const handlePopState = (event: PopStateEvent) => {
      const { isMobile, isTablet, downloading, hasDownloaded } =
        shouldBlockFactorsRef.current;
      if (!(isMobile || isTablet) && (downloading || !hasDownloaded)) {
        const confirmLeave = window.confirm(message);
        if (!confirmLeave) {
          // Cancel back by re-pushing the current path
          window.history.pushState(null, "", window.location.pathname);
        } else {
          navigate(`${EymemoryRoutes.EYMEMORIES_DOWNLOAD_LINK}/${orderHash}`);
        }
      }
    };

    // Trap the back button by adding a fake state
    window.history.pushState(null, "", window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [downloading, hasDownloaded, isMobile, isTablet]);

  // useEffect(() => {
  //   if (
  //     // secondsLeft <= 0 &&
  //     !hasDownloaded &&
  //     !downloading &&
  //     !isReDownloaded.current
  //   ) {
  //     handleDownload();
  //   }

  // if (secondsLeft > 0) {
  //   const timer = setInterval(() => {
  //     setSecondsLeft((prev: number) => prev - 1);
  //   }, 1000);

  //   return () => clearInterval(timer);
  // }
  // }, [
  // secondsLeft,
  //  hasDownloaded]);

  const downloadWillStart = () => {
    return (
      <div className="downloading-content mg-b-md-50">
        <p className="fs-5 fs-18 text-black fw-bold mg-b-5 text-center">
          Your order is now ready to download
        </p>
        <p className="fs-14 text-black text-center mg-b-10">
          {(isMobile || isTablet) &&
            "A zip file will be downloaded so we recommend doing this on a laptop or PC"}
          {/* <CustomButton
            text={"click here"}
            color="link"
            customBtnStyle="custom-btn-link-with-underline fs-14 fw-bold"
            onClick={handleDownload}
          /> */}
        </p>
        <div className="mg-t-30 downloading-content-btn">
          <CustomButton
            text="Start Download"
            color="primary"
            onClick={handleDownload}
          />
        </div>
      </div>
    );
  };

  const downloadProgressContent = () => {
    return (
      <div className="downloading-content mg-b-md-50">
        <p className="fs-5 fs-18 text-black fw-bold mg-b-5 text-center">
          Downloading your order
        </p>
        <p className="fs-14 text-black text-center mg-b-10">
          Please wait while your file is being prepared.
        </p>
        <div className="progress-bar-container">
          <CircularProgressbar
            value={progress}
            text={`${progress}%`}
            styles={buildStyles({
              // Colors
              pathColor: `rgb(76, 175, 80)`,
              textColor: "#000000",
              trailColor: "#d4d4d4",
            })}
          />
        </div>
      </div>
    );
  };

  const downloadCompletedContent = () => {
    const fileName = getFilenameFromUrl(downloadUrl) || "MyEarlyYearsStory.zip";
    return (
      <div className="downloading-content text-center mg-b-md-50">
        <div className="download-complete-img">
          <img src={LocalImages.successTick} alt="download complete" />
        </div>
        <p className="fs-5 fs-18 text-black fw-bold mg-b-5 text-center">
          Download Complete
        </p>
        <p className="fs-14 text-black text-center mg-b-10">
          Your zip file <strong>{fileName}</strong> has been successfully
          downloaded. <br />
          To open the file, please follow the steps provided in the order email
        </p>
        <div className="mg-t-30 downloading-content-btn">
          <CustomButton
            text="Done"
            color="primary"
            onClick={() =>
              navigate(
                `${EymemoryRoutes.EYMEMORIES_DOWNLOAD_LINK}/${orderHash}`
              )
            }
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {!downloading && !hasDownloaded && downloadWillStart()}
      {downloading && downloadProgressContent()}
      {hasDownloaded && downloadCompletedContent()}
    </>
  );
};

export default NegativeCountdownDownload;
